/**
 * The shared light/dark preference cookie (account portal slice 3, decision
 * D11; docs/architecture/shared-theme-preference.md).
 *
 * The web app and the Identity service read and write one first-party cookie
 * on the environment's parent domain, so a deliberate choice on either site
 * applies on both. It is a display preference with no security effect: one
 * allowlisted word, no identifier, written only on an explicit choice.
 *
 * Pure functions only, so the same rules can be unit-tested without a browser
 * and mirrored exactly by the `index.html` pre-paint script and Identity's
 * `ThemePreference`.
 */

export type ThemeCookieMode = 'light' | 'dark' | 'system';

/** One year, the contract's `Max-Age`. */
export const THEME_COOKIE_MAX_AGE_SECONDS = 31536000;

/**
 * The cookie names a deployment may configure: `syrf-theme` (production) or
 * `syrf-theme-<suffix>` (`syrf-theme-staging`, `syrf-theme-pr-4112`,
 * `syrf-theme-local`). The pattern makes a collision with the sign-in cookies
 * impossible by construction.
 */
export const THEME_COOKIE_NAME_PATTERN =
  /^syrf-theme(?:-[a-z0-9]+(?:-[a-z0-9]+)*)?$/;

/**
 * Cookies the theme cookie must never be confused with: the BFF session and
 * per-flow PKCE cookies and the silent sign-in guards (cross-cutting rule 8).
 * Asserted against {@link THEME_COOKIE_NAME_PATTERN} by the spec.
 */
export const RESERVED_SIGN_IN_COOKIE_NAMES = [
  'syrf-session',
  'syrf-pkce',
  'syrf-signed-out',
  'syrf-silent-auth',
] as const;

/** Production's registrable domain: never written outside production. */
export const PRODUCTION_REGISTRABLE_DOMAIN = 'syrf.org.uk';

/** localStorage key the pre-paint script reads to find the configured name. */
export const THEME_COOKIE_NAME_STORAGE_KEY = 'syrf-theme-cookie-name';

/** A resolved, validated cookie configuration. `domain` null means host-only. */
export interface ThemeCookieConfig {
  readonly name: string;
  readonly domain: string | null;
}

/** Returns the mode for an exact allowlisted value, otherwise null. */
export function parseThemeMode(
  value: string | null | undefined
): ThemeCookieMode | null {
  return value === 'light' || value === 'dark' || value === 'system'
    ? value
    : null;
}

/** True when `hostname` is `domain` or a subdomain of it (RFC 6265 domain-match). */
export function domainMatches(hostname: string, domain: string): boolean {
  const host = hostname.toLowerCase();
  const parent = domain.toLowerCase();
  return host === parent || host.endsWith(`.${parent}`);
}

/**
 * Validates the deployed configuration against the page's own host.
 *
 * - An invalid name disables the cookie entirely (`null`): the app keeps its
 *   own `localStorage` preference, as before slice 3.
 * - A domain that does not cover this host, or `syrf.org.uk` outside
 *   production, falls back to a host-only cookie, so a misconfiguration can
 *   never overwrite another environment's preference.
 */
export function resolveThemeCookieConfig(options: {
  name: string | null | undefined;
  domain: string | null | undefined;
  hostname: string;
  runtimeEnvironment: string | null | undefined;
}): ThemeCookieConfig | null {
  const name = (options.name ?? '').trim();
  if (!THEME_COOKIE_NAME_PATTERN.test(name)) {
    return null;
  }

  const domain = (options.domain ?? '').trim().replace(/^\./, '').toLowerCase();
  if (domain === '') {
    return { name, domain: null };
  }

  const isProduction =
    (options.runtimeEnvironment ?? '').toLowerCase() === 'production';
  const coversHost = domainMatches(options.hostname, domain);
  const reachesProduction =
    domain === PRODUCTION_REGISTRABLE_DOMAIN && !isProduction;
  return { name, domain: coversHost && !reachesProduction ? domain : null };
}

/**
 * Reads the mode from a `document.cookie` string. Only an exact cookie name and
 * an exact allowlisted value count; anything else is ignored.
 */
export function readThemeCookie(
  cookieHeader: string,
  name: string
): ThemeCookieMode | null {
  for (const part of cookieHeader.split(';')) {
    const separator = part.indexOf('=');
    if (separator < 0) continue;
    if (part.slice(0, separator).trim() !== name) continue;
    return parseThemeMode(part.slice(separator + 1).trim());
  }
  return null;
}

/** The `document.cookie` assignment for a choice. */
export function serializeThemeCookie(
  config: ThemeCookieConfig,
  mode: ThemeCookieMode
): string {
  const domain = config.domain ? `; Domain=${config.domain}` : '';
  return `${config.name}=${mode}${domain}; Path=/; Max-Age=${THEME_COOKIE_MAX_AGE_SECONDS}; SameSite=Lax; Secure`;
}
